import * as React from 'react';
import clsx from 'clsx';
import { useFloating, autoUpdate, arrow, flip, limitShift, shift } from '@floating-ui/react-dom-interactions';

// Layer acceleration can disable subpixel rendering which causes slightly
// blurry text on low PPI displays, so we want to use 2D transforms
// instead
// transform:
// (win.devicePixelRatio || 1) <= 1
//   ? `translate(${x}px, ${y}px)`
//   : `translate3d(${x}px, ${y}px, 0)`,

import { useCombinedRefs } from '@ovpn-ui/utils';
import Portal from '../../_internal/components/Portal';
import { StyledPopper, Arrow } from './Popper.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultElement = 'div';
function Popper(props, ref) {
  const {
    // content
    title,
    children,
    // behaviour
    anchorEl,
    open = false,
    // appearance
    className,
    style = {},
    as = defaultElement,
    placement = 'bottom',
    arrow: withArrow = false,
    // floating-ui props
    middlewares = [],
    overflowPadding = 0,
    arrowPadding = 0,
    ...htmlAttributes
  } = props;
  const arrowRef = React.useRef(null);
  const {
    x,
    y,
    update,
    reference,
    floating,
    strategy,
    middlewareData: {
      arrow: {
        x: arrowX,
        y: arrowY
      } = {}
    },
    placement: actualPlacement
  } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [flip({
      padding: overflowPadding
    }), shift({
      limiter: limitShift(),
      padding: overflowPadding
    }), arrow({
      element: arrowRef,
      padding: arrowPadding
    }), ...middlewares]
  });
  React.useLayoutEffect(() => {
    reference(anchorEl);
  }, [anchorEl, reference]);
  const combinedRef = useCombinedRefs(ref, floating);
  const arrowCallback = React.useCallback(node => {
    arrowRef.current = node;
    update();
  }, [update]);
  const content = title || React.Children.toArray(children);
  if (!open || !anchorEl || !content) return null;
  return /*#__PURE__*/_jsx(Portal, {
    children: /*#__PURE__*/_jsxs(StyledPopper, {
      className: clsx('ovpn-popper', className),
      forwardedAs: as,
      ref: combinedRef,
      style: {
        ...style,
        position: strategy,
        top: y != null ? y : 0,
        left: x != null ? x : 0
        // use transform: translate() instead to position the floating element for increased performance.
        // transform: `translate3d(${Math.round(x ?? 0)}px,${Math.round(y ?? 0)}px,0)`,
      },
      ...htmlAttributes,
      children: [withArrow && /*#__PURE__*/_jsx(Arrow, {
        className: "ovpn-popper-arrow",
        ref: arrowCallback,
        "data-placement": actualPlacement,
        style: {
          transform: `translate3d(${arrowX != null ? arrowX : 0}px, ${arrowY != null ? arrowY : 0}px, 0px)`
        }
      }), content]
    })
  });
}
export default /*#__PURE__*/React.forwardRef(Popper);